import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'

type BucketName = 'avatars' | 'designs' | 'thumbnails'

interface UploadResult {
  path: string
  url: string
}

export function useStorage() {
  const { user } = useAuthStore()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const upload = async (
    file: File,
    bucket: BucketName,
    path: string,
    upsert = false
  ): Promise<UploadResult | null> => {
    if (!user?.id) {
      setError('Not authenticated')
      return null
    }

    setIsUploading(true)
    setUploadProgress(0)
    setError(null)

    try {
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(path, file, { upsert, cacheControl: '3600' })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path)

      setUploadProgress(100)
      return { path: data.path, url: urlData.publicUrl }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      return null
    } finally {
      setIsUploading(false)
    }
  }

  const uploadAvatar = async (file: File): Promise<UploadResult | null> => {
    if (!user?.id) return null
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    return upload(file, 'avatars', `${user.id}/avatar.${ext}`, true)
  }

  const uploadDesignFile = async (file: File, projectId: string): Promise<UploadResult | null> => {
    if (!user?.id) return null
    return upload(file, 'designs', `${user.id}/${projectId}/${Date.now()}_${file.name}`)
  }

  const deleteFile = async (bucket: BucketName, path: string): Promise<boolean> => {
    const { error } = await supabase.storage.from(bucket).remove([path])
    if (error) { setError(error.message); return false }
    return true
  }

  const getPublicUrl = (bucket: BucketName, path: string): string => {
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl
  }

  return {
    upload,
    uploadAvatar,
    uploadDesignFile,
    deleteFile,
    getPublicUrl,
    isUploading,
    uploadProgress,
    error,
    clearError: () => setError(null),
  }
}
